<script setup lang="ts">
import type { PipelineStage } from '../../composables/useSignalPipeline'
// `provisional` = pure step-aside before the 08:40 print froze: the decision
// chip is rendered muted/dashed so it is not read as final.
defineProps<{ stages: PipelineStage[]; decision: 'buy' | 'sell' | 'hold' | null; provisional?: boolean }>()
defineEmits<{ explain: [key: string] }>()

// Value colour is state-aware: on blocking/degraded/holding chips the value
// inherits the chip's state colour (red/amber) — a green st-pos inside a red
// blocking chip would contradict the "blocking stage turns red" rule. The
// sign heuristic only applies on active/inactive chips.
function valueClass(st: PipelineStage): string {
  if (st.state === 'blocking' || st.state === 'degraded' || st.state === 'holding') return ''
  return st.value?.startsWith('-') ? 'st-neg' : 'st-pos'
}
</script>

<template>
  <div class="pipeline" data-testid="signal-pipeline">
    <template v-for="(st, i) in stages" :key="st.key">
      <span v-if="i > 0" class="pl-arrow">→</span>
      <span class="stage" :class="['st-' + st.state, { 'st-provisional': st.provisional }]" :data-testid="st.testid" :title="st.title">
        <span class="st-label">{{ st.label }}</span>
        <b v-if="st.value" class="st-value" :class="valueClass(st)">{{ st.value }}</b>
        <span v-if="st.frozen" class="st-frozen" title="Decision-time snapshot — value latched, not live">❄</span>
        <span v-else-if="st.provisional" class="st-live" title="Still drifting — freezes at the 08:40 London print">●</span>
        <span v-if="st.detail" class="st-detail">{{ st.detail }}</span>
        <span v-if="st.explainerKey" class="st-info info-icon" @click.stop="$emit('explain', st.explainerKey!)">i</span>
      </span>
    </template>
    <span class="pl-arrow">→</span>
    <span class="pl-decision" :class="[decision ? 'pl-' + decision : '', { 'pl-provisional': provisional }]">{{ decision?.toUpperCase() ?? '--' }}</span>
  </div>
</template>

<style scoped>
.pipeline { display: flex; align-items: center; gap: 4px; flex-wrap: wrap;
  background: #161b22; border: 1px solid #30363d; border-radius: 8px;
  padding: 8px 12px; font-family: 'JetBrains Mono', monospace; font-size: .72rem; color: #c9d1d9; }
.pl-arrow { color: #484f58; }
.stage { display: inline-flex; align-items: baseline; gap: 5px;
  background: #0d1117; border: 1px solid #30363d; border-radius: 6px; padding: 4px 8px; }
.st-inactive { border-style: dashed; border-color: #21262d; color: #6e7681; }
.st-active[data-testid="fva-decomp-row"] { border-color: #a371f7; }
.st-blocking { background: #2a0a0a; border-color: #f85149; color: #f85149; }
.st-holding { border-color: #d29922; color: #d29922; }
.st-degraded { background: #2a1a0a; border-color: #d29922; color: #d29922; }
.st-pos { color: #3fb950; } .st-neg { color: #f85149; }
.st-detail { color: #8b949e; }
.st-frozen { font-size: .65rem; }
.st-live { font-size: .55rem; color: #3fb950; animation: st-pulse 1.6s ease-in-out infinite; }
@keyframes st-pulse { 0%, 100% { opacity: .35; } 50% { opacity: 1; } }
.st-provisional { border-style: dashed; }
.pl-provisional { opacity: .6; outline: 1px dashed #8b949e; outline-offset: 2px; }
.st-info { cursor: pointer; color: #58a6ff; border: 1px solid #58a6ff; border-radius: 50%;
  width: 12px; height: 12px; line-height: 12px; text-align: center; font-size: .6rem; display: inline-block; }
.pl-decision { border-radius: 6px; padding: 4px 10px; font-weight: 700; color: #fff; background: #30363d; }
.pl-buy { background: #238636; } .pl-sell { background: #da3633; } .pl-hold { background: #9e6a03; }
</style>
